import { toast } from "vue3-toastify";
import { axios_instance } from "./api";

export const dumpCredential = async (agentId: string): Promise<void> => {
  const formData = new FormData();
  formData.append("agent_id", agentId);

  const response = await axios_instance.post(
    "/api/v1/view/action/dump-credential",
    formData,
  );

  if (response.data.message === "ok") {
    toast.success("Đã gửi yêu cầu Trích xuất mật khẩu!");
  } else {
    toast.error(response.data.message || "Không thể bắt đầu Trích xuất mật khẩu!");
  }
};

export const downloadDumpCredentialFile = async (
  agentId: string,
  path: string,
): Promise<void> => {
  try {
    const response = await axios_instance.get(
      `/api/v1/view/dump-credential/${agentId}/${encodeURIComponent(path)}`,
      {
        responseType: "blob",
      },
    );

    if (!response || !response.data) {
      toast.error("Không có dữ liệu để tải xuống.");
      return;
    }

    const status = response.status ?? 0;
    const blob = new Blob([response.data], {
      type: String(
        response.headers["content-type"] ?? "application/octet-stream",
      ),
    });

    if (status === 200 && blob.size > 0) {
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const disposition = String(
        response.headers["content-disposition"] ?? "",
      );
      const filename =
        disposition.match(/filename="?([^"]+)"?/)?.[1] ??
        path.split("/").pop() ??
        "download";
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      const errText = await new Response(response.data).text();
      const match = errText.match(/"message":"([^"]+)"/);
      const message = match ? match[1] : errText || "Tải xuống thất bại.";
      toast.error(message);
    }
  } catch (err) {
    toast.error(
      err instanceof Error ? err.message : "Không thể tải xuống.",
    );
  }
};

// ponytail: shared validation for the inject form surface — both the dropdown
// quick-submit and the dedicated page branch identically on inject_type.
export const injectFormValid = (values: {
  type: "reflective_dll" | "masquerading" | "process_hollowing";
  exportName?: string;
  shellcodeB64?: string;
  spoofPath?: string;
  path?: string;
}): boolean => {
  switch (values.type) {
    case "reflective_dll":
      // ponytail: no DLL path any more — the module is uploaded or picked from
      // the saved list, so only the export name is required here.
      return !!values.exportName;
    case "masquerading":
      return !!values.shellcodeB64 && !!values.spoofPath;
    case "process_hollowing":
      return !!values.path;
    default:
      return false;
  }
};

// Inject action — POST /api/v1/view/process/inject
export const createInjectAction = async ({
  agent_id,
  pid,
  inject_type,
  export_name,
  shellcode_b64,
  spoof_path,
  path,
  module_b64,
  version_id,
  original_file_name,
}: {
  agent_id: string;
  pid: string;
  inject_type: "reflective_dll" | "masquerading" | "process_hollowing";
  export_name?: string;
  shellcode_b64?: string;
  spoof_path?: string;
  path?: string;
  module_b64?: string;
  version_id?: string;
  original_file_name?: string;
}) => {
  // ponytail: backend registers view-side inject under /view/process/inject
  // (server.go viewProcess.POST("/inject")), not /view/action/inject.
  // ponytail: inject module resolution — backend accepts either an inline
  // base64 upload (module_b64) or a reuse of an existing Version record
  // (version_id). reflective_dll/masquerading share bof_reflective; process
  // hollowing uses bof_hollowing. Both are optional at the payload level but
  // exactly one is required by backend per inject_type; the UI always supplies
  // one via the module picker below.
  return axios_instance.post("/api/v1/view/process/inject", {
    agent_id,
    pid,
    inject_type,
    ...(inject_type === "reflective_dll" && { export_name }),
    ...(inject_type === "masquerading" && { shellcode_b64, spoof_path }),
    ...(inject_type === "process_hollowing" && { path }),
    ...((module_b64 || version_id) && { module_b64, version_id, original_file_name }),
  });
};

// ponytail: the backend stores inject DLLs under reflective_exe / hollowing_exe
// (see process.go storeInjectModule + resolveInjectModule). Older rows uploaded
// from Version Control sit in bof_reflective / bof_hollowing, so the lister
// asks for both — a saved module shows up no matter which flow uploaded it.
export const injectModuleType = {
  reflective_dll: ["reflective_exe", "bof_reflective"],
  masquerading: ["reflective_exe", "bof_reflective"],
  process_hollowing: ["hollowing_exe", "bof_hollowing"],
} as const;

export type InjectModuleType = (typeof injectModuleType)[keyof typeof injectModuleType];

export const listInjectModules = async ({
  types,
  page = 1,
  size = 24,
  keyword,
}: {
  types: readonly string[];
  page?: number;
  size?: number;
  keyword?: string;
} = {} as never): Promise<SavedVersion[]> => {
  const response = await axios_instance.post<{ data: Record<string, unknown>[] }>(
    "/api/v1/view/version/pagination",
    { type: [...types], page, size, keyword },
  );
  return (response.data.data ?? []).map((row) => ({
    id: row.id as string | number,
    version: String(row.version),
    filename: (row.original_file_name || row.file_name) as string | undefined,
    description: row.description as string | undefined,
  }));
};

export interface InjectActionHistory {
  id: string;
  status: string;
  type: number;
  created: number;
  log: string;
  target_path: string;
  target_pid: string;
}

export const listInjectActions = async ({
  agent_id,
  page,
  size = 20,
}: {
  agent_id: string;
  page: number;
  size?: number;
}): Promise<PaginatedResponse<InjectActionHistory>> => {
  const response = await axios_instance.post<PaginatedResponse<InjectActionHistory> & { message: string }>(
    "/api/v1/view/process/inject-actions",
    { agent_id, page, size },
  );
  return response.data;
};

export interface DumpCredentialAction {
  id: string;
  agent_id: string;
  type: string;
  status: string;
  created: number;
  modified: number;
}

export interface DumpCredentialItem {
  id: string;
  agent_id: string;
  scan_id: string;
  data: string;
  type: string;
  created: number;
  modified: number;
}

export interface PaginatedResponse<T> {
  data: T[] | null;
  total_filter: number;
}

export const listDumpCredentialActions = async (
  agentId: string,
  keyword: string,
  page: number,
  size: number,
): Promise<PaginatedResponse<DumpCredentialAction>> => {
  const response = await axios_instance.post<
    PaginatedResponse<DumpCredentialAction>
  >("/api/v1/view/dump-credential/actions", {
    agent_id: agentId,
    keyword,
    page,
    size,
  });
  return response.data;
};

export const listDumpCredentialCredentials = async (
  agentId: string,
  scanId: string,
  keyword: string,
  credType: string,
  page: number,
  size: number,
): Promise<PaginatedResponse<DumpCredentialItem>> => {
  const response = await axios_instance.post<
    PaginatedResponse<DumpCredentialItem>
  >("/api/v1/view/dump-credential/pagination", {
    agent_id: agentId,
    scan_id: scanId,
    keyword,
    cred_type: credType,
    page,
    size,
  });
  return response.data;
};

export type ElevateAction = "elevate_admin" | "elevate_system";

// ponytail: storage type per privilege target, mirrors backend defs.ElevateAdminModuleType /
// ElevateSystemModuleType ("elevate-admin"/"elevate-system"). Elevation must scope the saved-file
// picker to exactly one of these — never the generic "elevate" bucket.
export const elevateModuleType = {
  elevate_admin: "elevate-admin",
  elevate_system: "elevate-system",
} as const;

export interface SavedVersion {
  id: string | number;
  version: string;
  filename?: string;
  description?: string;
}

export const file_to_module_b64 = async (file: File): Promise<string> => {
  const b64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // strip data-URL prefix before sending (backend rejects bad base64)
      const result = reader.result as string;
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  return b64;
};

export const createElevateAction = async ({
  agentId,
  action,
  description,
  version,
  module_b64,
  original_file_name,
}: {
  agentId: string;
  action: ElevateAction;
  description?: string;
  version?: string;
  module_b64?: string;
  original_file_name?: string;
}): Promise<string> => {
  const response = await axios_instance.post<{
    message: string;
    action_id: string;
    status: string;
  }>("/api/v1/view/action/elevate", {
    agent_id: agentId,
    action,
    description,
    version,
    module_b64,
    original_file_name,
  });
  return response.data.action_id;
};

export const listElevateVersions = async ({
  type,
  page = 1,
  size = 24,
  keyword,
}: {
  type: "elevate-admin" | "elevate-system";
  page?: number;
  size?: number;
  keyword?: string;
} = {} as never): Promise<SavedVersion[]> => {
  const response = await axios_instance.post<{ data: Record<string, unknown>[] }>(
    "/api/v1/view/version/pagination",
    { type: [type], page, size, keyword },
  );
  return (response.data.data ?? []).map((row) => ({
    id: row.id as string | number,
    version: String(row.version),
    // ponytail: storage file_name remains the internal .bin object key; display the uploaded name when present.
    filename: (row.original_file_name || row.file_name) as string | undefined,
    description: row.description as string | undefined,
  }));
};
